import React, { useState } from 'react';
import useIntegratePOSSquareStore from '../../../stores/integratePOSSquareStore';
import { Checkbox, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Square Prerequisites Content
export const SquarePrerequisitesContent = ({ onNext, onBack, onClose }) => {
  const { setSquareAccountVerified } = useIntegratePOSSquareStore();

  const [completedSteps, setCompletedSteps] = useState({
    verifyAccount: false,
    checkSubscription: false,
    confirmPermissions: false,
    readyToSetup: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setSquareAccountVerified(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Square POS Integration Setup
        </h2>
        <p className="text-gray-600">
          Let's prepare for your Square POS integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Prerequisites for Square Integration</h3>

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.verifyAccount}
              onChange={(e) => handleStepChange('verifyAccount', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Verify Square Account</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Ensure you have an active <span className="font-medium">Square</span> account with API access enabled.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    You should be able to log into your Square Dashboard at <span className="font-mono text-sm">squareup.com</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.checkSubscription}
              onChange={(e) => handleStepChange('checkSubscription', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Check API Access Plan</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Confirm that your Square subscription includes <span className="font-medium">API access</span> and third-party integrations.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    Most Square plans include API access, but some limitations may apply to basic plans.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.confirmPermissions}
              onChange={(e) => handleStepChange('confirmPermissions', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Confirm Account Owner Permissions</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Verify that your account has <span className="font-medium">Account Owner</span> or <span className="font-medium">Admin</span> level permissions.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    These permissions are required to create applications and generate API credentials for your Square locations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.readyToSetup}
              onChange={(e) => handleStepChange('readyToSetup', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">4.</span>
                <div>
                  <p className="font-medium text-gray-900">Ready for App Setup</p>
                  <p className="text-gray-600 text-sm mt-1">
                    I'm ready to navigate to the Square Developer Portal and create an application for Rightwork integration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to App Setup
        </button>
      </div>
    </div>
  );
};

// Square App Setup Content
export const SquareAppSetupContent = ({ onNext, onBack, onClose }) => {
  const { setAppSetupCompleted } = useIntegratePOSSquareStore();

  const [completedSteps, setCompletedSteps] = useState({
    navigateToDeveloper: false,
    createApplication: false,
    configureApp: false,
    generateToken: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setAppSetupCompleted(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Create Square Application
        </h2>
        <p className="text-gray-600">
          Set up a Square application for API access
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Generate Square API Credentials</h3>

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.navigateToDeveloper}
              onChange={(e) => handleStepChange('navigateToDeveloper', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Access Square Developer Portal</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Go to <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">developer.squareup.com</span> and log in with your Square account
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.createApplication}
              onChange={(e) => handleStepChange('createApplication', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Create New Application</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Click <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">+ Create your first application</span> or <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">New Application</span></p>
                    <p className="mt-1.5">Application Name: <span className="font-mono bg-white border px-2 py-1 rounded text-sm">Rightwork Integration</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.configureApp}
              onChange={(e) => handleStepChange('configureApp', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Configure Application Settings</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Select application type: <span className="font-medium">Custom application</span></p>
                    <p className="mt-1">Choose <span className="font-medium">Production</span> environment for live data access</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              checked={completedSteps.generateToken}
              onChange={(e) => handleStepChange('generateToken', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": { color: "#6d28d9" },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">4.</span>
                <div>
                  <p className="font-medium text-gray-900">Generate Access Token</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Navigate to <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Production</span> tab → Copy your <span className="font-medium">Application ID</span> and <span className="font-medium">Access Token</span></p>
                    <p className="text-amber-700 mt-1 font-medium">⚠️ Keep both credentials secure!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold">Production vs Sandbox:</p>
            <p>Make sure you're using the <span className="font-medium">Production</span> credentials, not Sandbox. Sandbox credentials won't work with real business data.</p>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Credential Input
        </button>
      </div>
    </div>
  );
};

// Square Credential Input Content
export const SquareCredentialInputContent = ({ onBack, onClose, companyId, onIntegrationComplete }) => {
  const { setFormData, formData, integrateSquare } = useIntegratePOSSquareStore();

  const [credentials, setCredentials] = useState({
    applicationId: formData.applicationId || '',
    accessToken: formData.accessToken || ''
  });
  const [showToken, setShowToken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const allFieldsCompleted = Object.values(credentials).every(value => value.trim().length > 0);

  const handleCredentialChange = (field, value) => {
    const updatedCredentials = {
      ...credentials,
      [field]: value
    };
    setCredentials(updatedCredentials);
    setFormData(field, value);
  };

  const handleComplete = async () => {
    if (allFieldsCompleted && !isLoading) {
      setIsLoading(true);
      try {
        const result = await integrateSquare(companyId);

        if (result.success) {
          if (typeof onIntegrationComplete === 'function') {
            try {
              await onIntegrationComplete();
            } catch (refreshError) {
              console.error('Error refreshing integration data:', refreshError);
            }
          }
          alert('Square integration completed successfully!');
          onClose();
        } else {
          alert(result.error || 'There was an error setting up the integration. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Error setting up Square integration:', error);
        alert('There was an error setting up the integration. Please try again or contact support.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="p-8 relative">
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Enter Square API Credentials
        </h2>
        <p className="text-gray-600">
          Input your Square API credentials to complete the integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-4">Enter Your API Credentials</h3>

        <div className="space-y-4 text-sm text-gray-700 mb-6">
          <p>Enter the Application ID and Access Token from your Square Developer Portal.</p>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">Need help finding your credentials?</h4>
            <ol className="space-y-2 text-sm">
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">1.</span>
                <span>Go to <span className="font-medium">developer.squareup.com</span> and open your Rightwork Integration app</span>
              </li>
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">2.</span>
                <span>Navigate to the <span className="font-medium">Production</span> tab</span>
              </li>
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">3.</span>
                <span>Copy the Application ID and Access Token from the credentials section</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="application-id" className="block text-sm font-medium text-gray-700 mb-2">
              Application ID <span className="text-red-500">*</span>
            </label>
            <TextField
              id="application-id"
              fullWidth
              variant="outlined"
              size="small"
              value={credentials.applicationId}
              onChange={(e) => handleCredentialChange('applicationId', e.target.value)}
              placeholder="e.g., sq0idp-..."
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
            <p className="text-xs text-gray-600 mt-1">
              This is your Application ID from the Square Developer Portal
            </p>
          </div>

          <div>
            <label htmlFor="access-token" className="block text-sm font-medium text-gray-700 mb-2">
              Access Token <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <TextField
                id="access-token"
                fullWidth
                variant="outlined"
                size="small"
                type={showToken ? "text" : "password"}
                value={credentials.accessToken}
                onChange={(e) => handleCredentialChange('accessToken', e.target.value)}
                placeholder="Enter your access token..."
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    fontSize: "14px",
                    paddingRight: "40px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
              >
                {showToken ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              This is your Production Access Token from Square Developer Portal
            </p>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-green-800">
            <p className="font-semibold">Security:</p>
            <p>Your credentials are encrypted and stored securely. Rightwork follows industry best practices for API security and payment data protection.</p>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleComplete}
          disabled={!allFieldsCompleted || isLoading}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allFieldsCompleted && !isLoading
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Completing...
            </div>
          ) : (
            'Complete Integration'
          )}
        </button>
      </div>
    </div>
  );
};
